import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

import type { Site } from '@/lib/types';
import { normalizeSearch } from '@/lib/text/normalizeSearch';
import { SITE_SECTIONS } from '@/lib/siteSections';

const NO_EXTRAS: Array<{ value: string; label: string }> = [];

/**
 * 현장 고르기 — 누르면 검색칸과 묶음별 목록이 펴진다 (사용자 지시 2026-10-16).
 *
 * <p>묶음은 홈 현장 리스트 위젯과 같은 {@link SITE_SECTIONS} 다. 두 벌이면 한쪽만 고쳐진다.
 * 기본 select 는 목록 안에서 글자를 칠 수 없어 직접 그린다.
 *
 * <p>바깥을 누르거나 Esc 로 닫는다. 검색칸에서 Enter 는 첫 번째로 걸린 현장을 고른다.
 *
 * <p>{@code extras} 는 묶음 위에 서는 현장 아닌 항목이다(안전 일지의 「전체」 · 「본사」). 검색에도 걸린다.
 */
export function SiteSearchSelect({
  sites,
  value,
  onChange,
  width = '260px',
  extras = NO_EXTRAS,
  disabled = false,
  className = 'input-field',
  buttonStyle,
}: {
  sites: Site[];
  value: string;
  onChange: (siteId: string) => void;
  width?: string;
  extras?: Array<{ value: string; label: string }>;
  disabled?: boolean;
  /** 닫힌 단추의 칸 모양 — 부르는 화면의 입력칸과 맞춘다. */
  className?: string;
  buttonStyle?: CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const sections = useMemo(() => {
    const needle = normalizeSearch(query);
    const hits = needle ? sites.filter((site) => normalizeSearch(site.name).includes(needle)) : sites;
    return SITE_SECTIONS.map((section) => ({ ...section, sites: hits.filter(section.match) })).filter(
      (section) => section.sites.length > 0,
    );
  }, [sites, query]);

  const extraHits = useMemo(() => {
    const needle = normalizeSearch(query);
    return needle ? extras.filter((extra) => normalizeSearch(extra.label).includes(needle)) : extras;
  }, [extras, query]);

  const currentLabel = extras.find((extra) => extra.value === value)?.label ?? sites.find((site) => site.id === value)?.name;

  const pick = (siteId: string) => {
    onChange(siteId);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', width: '100%', maxWidth: width }}>
      <button
        type="button"
        className={className}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
        style={{
          ...buttonStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '6px',
          textAlign: 'left',
          width: '100%',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentLabel ?? ''}</span>
        <span aria-hidden style={{ fontSize: '10px', color: 'var(--text-muted)' }}>▾</span>
      </button>

      {open && (
        <div
          className="card"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 30,
            width: '100%',
            minWidth: '260px',
            padding: '6px',
          }}
        >
          <input
            className="input-field"
            autoFocus
            placeholder="현장 검색"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              const first = extraHits[0]?.value ?? sections[0]?.sites[0]?.id;
              if (first !== undefined) pick(first);
            }}
          />
          <div role="listbox" style={{ maxHeight: '320px', overflowY: 'auto', marginTop: '6px' }}>
            {sections.length === 0 && extraHits.length === 0 && (
              <p style={{ padding: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>검색 결과가 없습니다.</p>
            )}
            {extraHits.map((extra) => (
              <OptionButton key={extra.value} selected={extra.value === value} onClick={() => pick(extra.value)} indent="8px">
                {extra.label}
              </OptionButton>
            ))}
            {sections.map((section) => (
              <div key={section.key}>
                <div style={{ padding: '6px 8px 2px', fontSize: '11px', color: 'var(--text-muted)' }}>{section.label}</div>
                {section.sites.map((site) => (
                  <OptionButton key={site.id} selected={site.id === value} onClick={() => pick(site.id)} indent="16px">
                    {site.name}
                  </OptionButton>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OptionButton({
  selected,
  onClick,
  indent,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  indent: string;
  children: string;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onClick}
      className="btn-ghost"
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: `5px 8px 5px ${indent}`,
        fontSize: '13px',
        fontWeight: selected ? 700 : 400,
        background: selected ? 'var(--accent-bg)' : undefined,
      }}
    >
      {children}
    </button>
  );
}
